import { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, getDoc, doc, deleteDoc, query, updateDoc, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebaseconfig';
import { getAuth } from 'firebase/auth';
import Swal from 'sweetalert2';

const usePostManagement = (selectedArea) => {
  const [posts, setPosts] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'fecha', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postFormData, setPostFormData] = useState({
    fecha: '',
    obra: '',
    descripcion: '',
    costo: '',
    percentage: 0,
    area: '',
    subarea: '',
  });

  const fetchPosts = async (area) => {
    try {
      let collectionNames = [area];
      if (area === 'desarrollo humano') {
        collectionNames = ['desarrollo humano', 'educacion', 'deportes'];
      } else if (area === 'promocion social') {
        collectionNames = ['promocion social', 'cic', 'caf', 'terrenos'];
      } else if (area === 'obras y servicios publicos') {
        collectionNames = ['obras y servicios publicos', 'planeamiento urbano'];
      }

      const allPosts = [];
      for (const collectionName of collectionNames) {
        const q = query(collection(db, collectionName), orderBy(sortConfig.key, sortConfig.direction));
        const querySnapshot = await getDocs(q);
        const postsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          area: doc.data().area || collectionName,
          subarea: doc.data().subarea || '',
        }));
        allPosts.push(...postsData);
      }
      setPosts(allPosts);
    } catch (error) {
      console.error('Error fetching posts:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al obtener posts: ' + error.message,
      });
    }
  };

  useEffect(() => {
    if (selectedArea) {
      fetchPosts(selectedArea);
    }
  }, [selectedArea, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const filteredPosts = posts.filter(post => 
    post.obra.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddPost = () => {
    setEditingPost(null);
    setPostFormData({
      fecha: '',
      obra: '',
      descripcion: '',
      costo: '',
      percentage: 0,
      area: selectedArea,
      subarea: '',
    });
    setModalIsOpen(true);
  };

  const handleEditPost = async (post) => {
    setEditingPost(post);
    setPostFormData({
      fecha: post.fecha || '',
      obra: post.obra || '',
      descripcion: post.descripcion || '',
      costo: post.costo || '',
      percentage: post.percentage || 0,
      area: post.area || selectedArea,
      subarea: post.subarea || '',
    });
    try {
      const postRef = doc(db, post.area, post.id);
      const postDoc = await getDoc(postRef);
      if (postDoc.exists()) {
        const postData = postDoc.data();
        console.log('State of the post:', postData.state);
        setPostFormData(prevData => ({
          ...prevData,
          state: postData.state || ''
        }));
      } else {
        console.log('No such document!');
      }
    } catch (error) {
      console.error('Error fetching post state:', error);
    }
  
    setModalIsOpen(true);
  };

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
  
      if (!currentUser) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Usuario no autenticado',
        });
        return;
      }
  
      const updatedPost = {
        ...postFormData,
        usuario: currentUser.email,
        costo: parseFloat(postFormData.costo),
        percentage: parseFloat(postFormData.percentage.toString()),
        area: postFormData.area || selectedArea,
        subarea: postFormData.subarea || '',
      };
  
      let collectionName = updatedPost.area;
  
      if (editingPost) {
        const postRef = doc(db, collectionName, editingPost.id);
        await updateDoc(postRef, updatedPost);
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: 'Post actualizado exitosamente',
        });
      } else {
        await addDoc(collection(db, collectionName), updatedPost);
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: 'Post creado exitosamente',
        });
      }
  
      setModalIsOpen(false);
      setEditingPost(null);
      setPostFormData({
        fecha: '',
        obra: '',
        descripcion: '',
        costo: '',
        percentage: 0,
        area: '',
        subarea: '',
      });
  
      fetchPosts(selectedArea);
    } catch (error) {
      console.error('Error al guardar el post:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al guardar el post: ' + error.message,
      });
    }
  };
  
  const handleDeletePost = async (post) => {
    try {
      await deleteDoc(doc(db, post.area, post.id));
      Swal.fire({
        icon: 'success',
        title: 'Éxito',
        text: 'Post eliminado exitosamente',
      });
      fetchPosts(selectedArea);
    } catch (error) {
      console.error('Error al eliminar el post:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al eliminar el post: ' + error.message,
      });
    }
  };

  return {
    posts: filteredPosts,
    sortConfig,
    handleSort,
    searchTerm,
    handleSearch,
    modalIsOpen,
    setModalIsOpen,
    editingPost,
    postFormData,
    setPostFormData,
    handleAddPost,
    handleEditPost,
    handlePostSubmit,
    handleDeletePost,
  };
};

export default usePostManagement;